import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { waitForVoices, speak, cancelSpeech, cues, isAcceptableVoice, pickDefaultVoice } from "../lib/speech";
import {
  User as UserIcon, GearSix, Crown, Ticket, Sparkle, Headphones, Target, Lightbulb,
  Question, SpeakerHigh, GraduationCap, ArrowRight, CheckCircle, Info, ChartBar, WarningCircle,
  Flame, Bell, Snowflake, Trophy,
} from "@phosphor-icons/react";
import Collapsible from "../components/Collapsible";
import DailyProgressRing from "../components/DailyProgressRing";
import useStats from "../lib/useStats";

const ENDPOINT_LABELS = {
  "convert-mcq": "MCQ → statement",
  "convert-mcq-bulk": "MCQ bulk",
  "convert-mcq-flashcard-bulk": "MCQ → flashcard",
  "chunk-text": "Text chunking",
  "rechunk": "Rebuild chunks",
  "match": "Concept match scoring",
  "generate-qa": "Generate Q&A from topic",
  "upload-extract": "Document import",
  "session-summary": "Session coach note",
  "hint": "AI hints",
};

export default function Profile() {
  const { user, setUser } = useAuth();
  const [prefs, setPrefs] = useState({});
  const [usage, setUsage] = useState(null);
  const [voices, setVoices] = useState([]);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { stats } = useStats();

  useEffect(() => {
    (async () => {
      try {
        const [p, u] = await Promise.all([api.get("/users/me/prefs"), api.get("/users/me/usage")]);
        setPrefs(p.data.prefs || {});
        setUsage(u.data);
      } catch {}
      waitForVoices().then(setVoices);
    })();
  }, []);

  const update = (key, val) => setPrefs((p) => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", prefs);
      toast.success("Preferences saved");
      // Also persist voice to localStorage for immediate use
      if (prefs.voice_uri) localStorage.setItem("echo_voice_uri", prefs.voice_uri);
      if (prefs.name) setUser({ ...user, name: prefs.name });
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await api.post("/promo/redeem", { code: code.trim() });
      toast.success(data.message || "Code redeemed!", { duration: 6000 });
      setCode("");
      // Refresh plan
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not redeem");
    } finally { setRedeeming(false); }
  };

  const cancelPlan = async () => {
    toast(`Switch from ${user.plan?.toUpperCase() || "paid"} to Free plan?`, {
      duration: 8000,
      action: {
        label: "Confirm switch",
        onClick: async () => {
          setCanceling(true);
          try {
            await api.post("/users/me/cancel-plan");
            toast.success("You're on the Free plan now. Thanks for giving Plus a try!");
            try {
              const me = await api.get("/auth/me");
              setUser(me.data);
            } catch {}
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not switch plan");
          } finally { setCanceling(false); }
        },
      },
      cancel: { label: "Keep my plan", onClick: () => {} },
    });
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ekkol-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 200);
      toast.success(`Exported ${data.counts?.decks || 0} decks, ${data.counts?.cards || 0} cards`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  const deleteAccount = () => {
    const confirmText = "delete my account";
    toast("Permanently delete your Ekkol account?", {
      description: `Type "${confirmText}" in the next prompt to confirm.`,
      duration: 8000,
      action: {
        label: "Continue",
        onClick: () => {
          const input = window.prompt(`Type "${confirmText}" to confirm. This is PERMANENT.`);
          if (input?.trim().toLowerCase() !== confirmText) {
            toast("Cancelled.");
            return;
          }
          (async () => {
            try {
              await api.delete("/users/me?confirm=DELETE");
              toast.success("Account deleted. Goodbye — come back anytime!");
              localStorage.removeItem("ekkol_token");
              localStorage.removeItem("echostudy_token");
              setTimeout(() => { window.location.href = "/"; }, 1200);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Delete failed — contact support@ekkol.app");
            }
          })();
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  if (!user) return null;

  const planBadgeMap = {
    free: { label: "FREE", color: "var(--ink-muted)" },
    plus: { label: "PLUS", color: "var(--oxford)" },
    team: { label: "EDU", color: "var(--vermilion)" },
    edu:  { label: "EDU", color: "var(--vermilion)" },
  };
  const planBadge = planBadgeMap[user.plan || "free"] || planBadgeMap.free;

  return (
    <div className="min-h-screen bg-[color:var(--paper)]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 fade-in">
        <div className="flex items-center gap-3 mb-4">
          <UserIcon size={22} className="text-[color:var(--oxford)]"/>
          <span className="eyebrow">Your account</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none">Profile &amp; preferences</h1>

        {/* Account card */}
        <div className="mt-10 grid md:grid-cols-3 gap-px bg-[color:var(--rule)] border rule">
          <div className="bg-white p-6 md:col-span-2 flex items-center gap-5">
            {user.picture && <img src={user.picture} alt="" className="w-16 h-16 rounded-full border rule"/>}
            <div className="flex-1">
              <input
                value={prefs.name ?? user.name ?? ""}
                onChange={(e) => update("name", e.target.value)}
                className="font-serif text-2xl w-full bg-transparent border-b rule py-1 focus:outline-none focus:border-[color:var(--ink)]"
                data-testid="profile-name"
              />
              <div className="font-mono text-xs text-[color:var(--ink-muted)] mt-1">{user.email}</div>
            </div>
          </div>
          <div className="bg-white p-6 flex flex-col justify-center">
            <div className="eyebrow mb-2">Plan</div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-2xl" style={{color: planBadge.color}}>{planBadge.label}</span>
              {user.is_super && <Crown size={18} className="text-[color:var(--vermilion)]"/>}
              {user.is_admin && !user.is_super && <span className="eyebrow text-[color:var(--oxford)]">Admin</span>}
            </div>
            <Link to="/pricing" className="text-xs link-underline mt-3" data-testid="profile-upgrade">
              View plans & upgrade →
            </Link>
          </div>
        </div>

        {/* Streak, daily goal & reminders (gamification + notifications) */}
        <Collapsible
          icon={<Flame size={18} className="text-orange-500"/>}
          title="Streak, daily goal &amp; reminders"
          subtitle="How much you want to study each day — plus optional nudges if you ask for them"
          defaultOpen={true}
          persistKey="pref-streak"
          testId="section-streak-goals"
          anchorId="streak"
        >
          {/* Streak snapshot */}
          <div className="grid sm:grid-cols-3 gap-px bg-[color:var(--rule)] border rule mb-6" data-testid="streak-snapshot">
            <div className="bg-white p-5 flex items-center gap-4">
              <DailyProgressRing cards={stats?.cards_today || 0} goal={stats?.daily_goal || prefs.daily_goal || 10} size={64} />
              <div>
                <div className="eyebrow">Today</div>
                <div className="font-mono text-xs text-[color:var(--ink-muted)] mt-1">cards studied</div>
              </div>
            </div>
            <div className="bg-white p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
                <Flame size={20} weight={stats?.at_risk ? "regular" : "fill"} className={stats?.at_risk ? "text-[color:var(--vermilion)]" : "text-orange-500"} />
              </div>
              <div>
                <div className="eyebrow">Streak</div>
                <div className="font-mono text-2xl">{stats?.streak || 0}</div>
                <div className="font-mono text-[10px] text-[color:var(--ink-muted)]">longest: {stats?.longest_streak || 0}</div>
              </div>
            </div>
            <div className="bg-white p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-sky-50 flex items-center justify-center">
                <Snowflake size={20} weight="fill" className="text-sky-500" />
              </div>
              <div>
                <div className="eyebrow">Freezes banked</div>
                <div className="font-mono text-2xl">{stats?.freezes || 0} <span className="text-xs text-[color:var(--ink-muted)]">/ 2</span></div>
                <div className="font-mono text-[10px] text-[color:var(--ink-muted)] italic">+1 every 7 days</div>
              </div>
            </div>
          </div>

          {/* Last 7 days heatmap */}
          {stats?.week && (
            <div className="mb-6">
              <div className="eyebrow mb-2">Last 7 days</div>
              <div className="flex gap-1.5" data-testid="week-heatmap">
                {stats.week.map((d) => {
                  const level = d.cards === 0 ? 0 : d.cards < (stats.daily_goal / 2) ? 1 : d.met_goal ? 3 : 2;
                  const cls = level === 0
                    ? "bg-[color:var(--surface-alt)]"
                    : level === 1
                      ? "bg-orange-200"
                      : level === 2
                        ? "bg-orange-400"
                        : "bg-emerald-500";
                  const dow = new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" });
                  return (
                    <div
                      key={d.date}
                      className={`flex-1 h-10 ${cls} border rule flex items-end justify-center pb-1`}
                      title={`${d.date}: ${d.cards} cards${d.met_goal ? " · goal met" : ""}`}
                    >
                      <span className="text-[9px] font-mono uppercase text-white mix-blend-difference">{dow[0]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Daily goal picker */}
          <div className="mb-6">
            <label className="eyebrow block mb-1">Daily goal</label>
            <p className="text-[11px] text-[color:var(--ink-muted)] italic mb-3">How many cards you want to clear each day to keep your streak. Pick what feels sustainable — 5 is fine, 10 is classic, 20 is ambitious.</p>
            <div className="grid grid-cols-4 gap-px bg-[color:var(--rule)] border rule max-w-md" data-testid="daily-goal-picker">
              {[5, 10, 20].map((n) => {
                const active = (prefs.daily_goal ?? stats?.daily_goal ?? 10) === n;
                return (
                  <button
                    key={n}
                    onClick={() => update("daily_goal", n)}
                    className={`p-3 text-center transition-colors ${active ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-75 hover:opacity-100"}`}
                    data-testid={`goal-preset-${n}`}
                  >
                    <div className="font-mono text-lg">{n}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest">cards</div>
                  </button>
                );
              })}
              <div className="bg-white p-2 flex flex-col items-center justify-center">
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={prefs.daily_goal ?? stats?.daily_goal ?? ""}
                  onChange={(e) => update("daily_goal", Math.max(1, Math.min(200, parseInt(e.target.value || "0", 10) || 1)))}
                  className="w-16 text-center font-mono text-lg bg-transparent border-b rule focus:outline-none focus:border-[color:var(--ink)]"
                  data-testid="goal-custom-input"
                  placeholder="?"
                />
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-muted)] mt-1">custom</div>
              </div>
            </div>
          </div>

          {/* Reminder toggles */}
          <div className="border-t rule pt-5">
            <div className="flex items-center gap-2 mb-2">
              <Bell size={14} className="text-[color:var(--oxford)]" />
              <span className="eyebrow">Reminders</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--ink-muted)] border rule px-1.5 py-0.5">opt-in</span>
            </div>
            <p className="text-[11px] text-[color:var(--ink-muted)] italic mb-4">Everything off by default. We will never email you without your say-so.</p>

            <div className="space-y-3" data-testid="reminder-toggles">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!prefs.remind_daily}
                  onChange={(e) => update("remind_daily", e.target.checked)}
                  className="mt-1 accent-[color:var(--ink)]"
                  data-testid="toggle-remind-daily"
                />
                <div className="flex-1">
                  <div className="text-sm">Daily study reminder</div>
                  <div className="text-[11px] text-[color:var(--ink-muted)] italic">A friendly in-app nudge (and browser push, if you allow it) at your chosen time each day.</div>
                  {prefs.remind_daily && (
                    <div className="mt-2 flex items-center gap-3">
                      <label className="text-xs text-[color:var(--ink-muted)]">Remind me at</label>
                      <input
                        type="time"
                        value={prefs.remind_time || "19:00"}
                        onChange={(e) => update("remind_time", e.target.value)}
                        className="bg-transparent border rule px-2 py-1 text-xs font-mono"
                        data-testid="reminder-time-input"
                      />
                    </div>
                  )}
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!prefs.remind_streak_risk}
                  onChange={(e) => update("remind_streak_risk", e.target.checked)}
                  className="mt-1 accent-[color:var(--ink)]"
                  data-testid="toggle-remind-streak"
                />
                <div className="flex-1">
                  <div className="text-sm">Streak-at-risk alert</div>
                  <div className="text-[11px] text-[color:var(--ink-muted)] italic">Only if your streak is 3+ days and you haven&apos;t studied by 8pm. Low-volume by design.</div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!prefs.remind_assignments}
                  onChange={(e) => update("remind_assignments", e.target.checked)}
                  className="mt-1 accent-[color:var(--ink)]"
                  data-testid="toggle-remind-assignments"
                />
                <div className="flex-1">
                  <div className="text-sm">Assignment due reminders</div>
                  <div className="text-[11px] text-[color:var(--ink-muted)] italic">If a class assignment is due in &lt;24 hours and you haven&apos;t studied the deck yet.</div>
                </div>
              </label>
            </div>

            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <button
                onClick={async () => {
                  if (!("Notification" in window)) {
                    toast.error("Browser push isn't supported here — the in-app banner will still work.");
                    return;
                  }
                  try {
                    const perm = await Notification.requestPermission();
                    if (perm === "granted") {
                      new Notification("Ekkol reminders enabled", { body: "Nice — we'll nudge you when it's time to study." });
                      toast.success("Browser notifications enabled");
                    } else {
                      toast("Permission declined — in-app banners will still work.");
                    }
                  } catch { toast.error("Could not request permission"); }
                }}
                className="btn-ghost text-xs"
                data-testid="btn-enable-push"
              >
                <Bell size={12} /> Enable browser notifications
              </button>
              <span className="text-[11px] text-[color:var(--ink-muted)] italic">Optional. You can still get in-app banners without this.</span>
            </div>

            <div className="mt-6 pt-5 border-t rule">
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-streak-prefs">
                <Sparkle size={14} weight="fill" /> {saving ? "Saving…" : "Save streak & reminder settings"}
              </button>
            </div>
          </div>
        </Collapsible>

        {/* Preferences — primary section, expanded by default */}
        <Collapsible
          icon={<GearSix size={18} className="text-[color:var(--oxford)]"/>}
          title="Study preferences"
          subtitle="How Ekkol sounds, scores, and paces — change anything here and it sticks"
          defaultOpen={true}
          persistKey="pref-study"
          testId="section-study-prefs"
        >
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <label className="eyebrow block mb-1">Default voice</label>
              <p className="text-[11px] text-[color:var(--ink-muted)] italic mb-2">Pick the voice Ekkol reads cards with. We default to <strong>Google US English</strong> because it sounds the most natural. Microsoft voices are hidden — they sounded too robotic to our testers.</p>
              <select
                value={prefs.voice_uri || ""}
                onChange={(e) => update("voice_uri", e.target.value)}
                className="w-full bg-transparent border rule px-2 py-2 text-sm"
                data-testid="pref-voice"
              >
                <option value="">System default ({pickDefaultVoice()?.name || "Google US English"})</option>
                {voices.filter(isAcceptableVoice).map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>{v.name}</option>
                ))}
              </select>
              <button
                onClick={() => speak("Today is a good day. The Battle of Guilford Courthouse was fought on March fifteenth, seventeen eighty-one. If you can hear this whole sentence clearly from the start, Ekkol is working correctly.", { voiceURI: prefs.voice_uri })}
                className="text-xs link-underline mt-2"
                data-testid="btn-preview-voice"
              >Preview voice · hear full sentence</button>
            </div>
            <div>
              <label className="eyebrow block mb-1">Default match mode</label>
              <p className="text-[11px] text-[color:var(--ink-muted)] italic mb-2"><strong>Exact</strong> = grades you on the exact words. <strong>Concept</strong> = AI understands paraphrases (say it in your own words and still get it right).</p>
              <div className="grid grid-cols-2 gap-px bg-[color:var(--rule)] border rule">
                <button
                  onClick={() => update("default_match_mode", "exact")}
                  className={`relative p-3 text-left transition-colors ${prefs.default_match_mode === "exact" ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-70 hover:opacity-100"}`}
                  data-testid="pref-match-exact"
                >
                  {prefs.default_match_mode === "exact" && <CheckCircle size={14} weight="fill" className="absolute top-2 right-2"/>}
                  <Target size={14}/> <span className="font-mono text-xs uppercase tracking-widest">Exact</span>
                </button>
                <button
                  onClick={() => update("default_match_mode", "concept")}
                  className={`relative p-3 text-left transition-colors ${prefs.default_match_mode === "concept" ? "bg-[color:var(--ink)] text-white" : "bg-white opacity-70 hover:opacity-100"}`}
                  data-testid="pref-match-concept"
                >
                  {prefs.default_match_mode === "concept" && <CheckCircle size={14} weight="fill" className="absolute top-2 right-2"/>}
                  <Lightbulb size={14}/> <span className="font-mono text-xs uppercase tracking-widest">Concept</span>
                </button>
              </div>
              <p className="text-[11px] text-[color:var(--ink-muted)] mt-2">Applies to new study sessions. Deck-specific settings override this.</p>
            </div>
            <div>
              <label className="eyebrow block mb-1">Default pass threshold · {prefs.default_match_threshold ?? 75}%</label>
              <p className="text-[11px] text-[color:var(--ink-muted)] italic mb-2">How close your answer needs to be to count as correct. 75% is a good starting point — raise it to be stricter, lower it if you keep getting frustrated.</p>
              <input
                type="range" min="40" max="100" step="5"
                value={prefs.default_match_threshold ?? 75}
                onChange={(e) => update("default_match_threshold", Number(e.target.value))}
                className="w-full accent-[color:var(--ink)]"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1">Default chunk size</label>
              <p className="text-[11px] text-[color:var(--ink-muted)] italic mb-2">When AI splits text into cards, how big each chunk should be. <strong>Short</strong> is easier to memorize word-for-word; <strong>Long</strong> is better once you know the material.</p>
              <div className="grid grid-cols-3 gap-px bg-[color:var(--rule)] border rule">
                {["short", "medium", "long"].map((s) => (
                  <button
                    key={s}
                    onClick={() => update("default_chunk_size", s)}
                    className={`relative p-3 text-center transition-colors ${prefs.default_chunk_size === s ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70 hover:opacity-100"}`}
                    data-testid={`pref-chunk-${s}`}
                  >
                    {prefs.default_chunk_size === s && <CheckCircle size={12} weight="fill" className="absolute top-1 right-1"/>}
                    <span className="font-mono text-xs uppercase tracking-widest">{s}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-2">When to listen</label>
              <div className="grid grid-cols-3 gap-px bg-[color:var(--rule)] border rule">
                {[
                  { k: "beep", label: "Wait for beep" },
                  { k: "auto", label: "Auto-start" },
                  { k: "bargein", label: "Barge-in" },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => update("listen_mode", m.k)}
                    className={`relative p-3 text-center transition-colors ${(prefs.listen_mode || "beep") === m.k ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70 hover:opacity-100"}`}
                    data-testid={`pref-listen-${m.k}`}
                  >
                    {(prefs.listen_mode || "beep") === m.k && <CheckCircle size={12} weight="fill" className="absolute top-1 right-1"/>}
                    <span className="font-mono text-[11px] uppercase tracking-widest">{m.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[color:var(--ink-muted)] mt-2">
                <strong>Wait for beep</strong>: reading finishes → beep → mic opens.{" "}
                <strong>Auto-start</strong>: mic opens silently the moment reading stops.{" "}
                <strong>Barge-in</strong>: mic listens while the app is still reading (great for multi-taskers, noisy for background noise).
              </p>
            </div>
            <div>
              <label className="eyebrow block mb-2">
                Beep volume · {Math.round((prefs.cue_volume ?? 1) * 100)}%
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range" min="0" max="1.5" step="0.1"
                  value={prefs.cue_volume ?? 1}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    update("cue_volume", v);
                    try { localStorage.setItem("echo_cue_volume", String(v)); } catch {}
                  }}
                  className="flex-1 accent-[color:var(--ink)]"
                  data-testid="pref-cue-volume"
                />
                <button
                  type="button"
                  onClick={() => cues.listenStart()}
                  className="btn-ghost py-2 px-3 text-xs"
                  data-testid="pref-cue-preview"
                >
                  <SpeakerHigh size={14}/> Test beep
                </button>
              </div>
              <p className="text-[11px] text-[color:var(--ink-muted)] mt-2">
                Two-tone tone that plays when it&apos;s your turn to speak. Set to 0% to disable entirely.
              </p>
            </div>
            <div>
              <label className="eyebrow block mb-2">If I miss a card…</label>
              <div className="grid grid-cols-3 gap-px bg-[color:var(--rule)] border rule">
                {[
                  { k: "off", label: "Never reveal" },
                  { k: "after3", label: "After 3 misses" },
                  { k: "endofrun", label: "At end of run" },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => update("reveal_answer_mode", m.k)}
                    className={`relative p-3 text-center transition-colors ${(prefs.reveal_answer_mode || "off") === m.k ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70 hover:opacity-100"}`}
                    data-testid={`pref-reveal-${m.k}`}
                  >
                    {(prefs.reveal_answer_mode || "off") === m.k && <CheckCircle size={12} weight="fill" className="absolute top-1 right-1"/>}
                    <span className="font-mono text-[11px] uppercase tracking-widest">{m.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[color:var(--ink-muted)] mt-2">Ekkol will read the correct answer aloud when triggered.</p>
            </div>
            <div>
              <label className="eyebrow block mb-2">Auto-hint after misses</label>
              <div className="grid grid-cols-4 gap-px bg-[color:var(--rule)] border rule">
                {[
                  { k: 0, label: "Off" },
                  { k: 1, label: "After 1" },
                  { k: 2, label: "After 2" },
                  { k: 3, label: "After 3" },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => update("auto_hint_after_n", m.k)}
                    className={`relative p-3 text-center transition-colors ${(prefs.auto_hint_after_n ?? 0) === m.k ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70 hover:opacity-100"}`}
                    data-testid={`pref-autohint-${m.k}`}
                  >
                    {(prefs.auto_hint_after_n ?? 0) === m.k && <CheckCircle size={12} weight="fill" className="absolute top-1 right-1"/>}
                    <span className="font-mono text-[11px] uppercase tracking-widest">{m.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[color:var(--ink-muted)] mt-2">
                Or ask on-demand: tap the 💡 <strong>Hint</strong> button — or say &ldquo;<em>hint</em>&rdquo; in hands-free. AI gets smarter each time. 30 hints/day on Free, 500/day on Plus.
              </p>
            </div>
            <label className="flex items-center gap-3 cursor-pointer md:col-span-2 border-t rule pt-4">
              <input
                type="checkbox"
                checked={prefs.hands_free_by_default ?? false}
                onChange={(e) => update("hands_free_by_default", e.target.checked)}
                className="w-4 h-4 accent-[color:var(--ink)]"
              />
              <span className="text-sm flex items-center gap-2"><Headphones size={14}/> Start study sessions in hands-free mode by default</span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer md:col-span-2 border-t rule pt-4" data-testid="voice-commands-toggle">
              <input
                type="checkbox"
                checked={prefs.voice_commands_enabled ?? true}
                onChange={(e) => update("voice_commands_enabled", e.target.checked)}
                className="w-4 h-4 accent-[color:var(--ink)] mt-0.5"
                data-testid="voice-commands-checkbox"
              />
              <span className="text-sm">
                <span className="flex items-center gap-2 font-medium">
                  <Headphones size={14}/> Enable voice commands during study
                </span>
                <span className="block text-[11px] text-[color:var(--ink-muted)] mt-1">
                  Say <em>&ldquo;next&rdquo;</em>, <em>&ldquo;back&rdquo;</em>, <em>&ldquo;repeat&rdquo;</em>, <em>&ldquo;hint&rdquo;</em>, <em>&ldquo;pause&rdquo;</em>, or <em>&ldquo;stop&rdquo;</em> to control the session hands-free.
                  In Learn mode, Ekkol only listens during the pause between reads (never while it&apos;s speaking). Turn this off if you prefer tap-only controls.
                </span>
              </span>
            </label>

            <div className="md:col-span-2 border-t rule pt-4" data-testid="attempts-before-reveal-section">
              <label className="eyebrow block mb-2">Reveal the answer after…</label>
              <div className="grid grid-cols-5 gap-px bg-[color:var(--rule)] border rule">
                {[
                  { k: 1, label: "1 miss" },
                  { k: 2, label: "2 misses" },
                  { k: 3, label: "3 misses" },
                  { k: 5, label: "5 misses" },
                  { k: 0, label: "Never" },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => update("attempts_before_auto_reveal", m.k)}
                    className={`relative p-3 text-center transition-colors ${(prefs.attempts_before_auto_reveal ?? 3) === m.k ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70 hover:opacity-100"}`}
                    data-testid={`pref-reveal-${m.k}`}
                  >
                    {(prefs.attempts_before_auto_reveal ?? 3) === m.k && <CheckCircle size={12} weight="fill" className="absolute top-1 right-1"/>}
                    <span className="font-mono text-[11px] uppercase tracking-widest">{m.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[color:var(--ink-muted)] mt-2">
                On a flashcard, Ekkol will speak the correct answer aloud after this many wrong attempts and push the card into your Retry pile. Set to <strong>Never</strong> to disable auto-reveal — you&apos;ll still have the <strong>Show answer</strong> button whenever you want it.
              </p>
            </div>

            {/* Learn mode defaults */}
            <div className="md:col-span-2 border-t rule pt-4">
              <label className="text-xs font-mono uppercase tracking-widest text-[color:var(--ink-muted)] block mb-2">
                Learn mode — listen &amp; repeat
              </label>
              <p className="text-[11px] text-[color:var(--ink-muted)] mb-3">
                Learn mode reads the card aloud, pauses for you to repeat, and auto-advances — no scoring, no mic needed. Set how long the pauses should be by default.
              </p>
              <div className="grid grid-cols-4 gap-px bg-[color:var(--rule)] border rule" data-testid="learn-pause-switch">
                {[
                  { k: "short", label: "Short · 3.5s" },
                  { k: "medium", label: "Medium · 6.5s" },
                  { k: "long", label: "Long · 10s" },
                  { k: "auto", label: "Auto · scale" },
                ].map((m) => (
                  <button
                    key={m.k}
                    onClick={() => update("learn_mode_pause", m.k)}
                    className={`relative p-3 text-center transition-colors ${(prefs.learn_mode_pause ?? "medium") === m.k ? "bg-[color:var(--ink)] text-white font-semibold" : "bg-white opacity-70 hover:opacity-100"}`}
                    data-testid={`learn-pause-${m.k}`}
                  >
                    {(prefs.learn_mode_pause ?? "medium") === m.k && <CheckCircle size={12} weight="fill" className="absolute top-1 right-1"/>}
                    <span className="font-mono text-[11px] uppercase tracking-widest">{m.label}</span>
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-3 cursor-pointer mt-3">
                <input
                  type="checkbox"
                  checked={prefs.learn_script_repeat ?? false}
                  onChange={(e) => update("learn_script_repeat", e.target.checked)}
                  className="w-4 h-4 accent-[color:var(--ink)]"
                />
                <span className="text-sm">Re-read each chunk on Script decks (second reading slightly slower)</span>
              </label>
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="btn-save-prefs">
              <CheckCircle size={16} weight="fill"/> {saving ? "Saving…" : "Save preferences"}
            </button>
          </div>
        </Collapsible>

        {/* Promo code */}
        <Collapsible
          icon={<Ticket size={18} className="text-[color:var(--oxford)]"/>}
          title="Redeem a code"
          subtitle="Paste a promo code to unlock Plus or Edu for a set period"
          defaultOpen={false}
          persistKey="promo"
          testId="section-promo"
        >
          <p className="text-sm text-[color:var(--ink-muted)] mb-3">Got a voucher? Paste it here to unlock Plus or Edu for a set number of days.</p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && redeem()}
              placeholder="ABCD-1234"
              className="bg-transparent border rule px-4 py-3 font-mono text-lg tracking-widest focus:outline-none focus:border-[color:var(--ink)]"
              data-testid="promo-code-input"
            />
            <button onClick={redeem} disabled={redeeming || !code.trim()} className="btn-primary" data-testid="promo-redeem-btn">
              <Sparkle size={16} weight="fill"/> {redeeming ? "Redeeming…" : "Redeem"}
            </button>
          </div>
        </Collapsible>

        {/* Plan & billing */}
        <Collapsible
          icon={<Crown size={18} className="text-[color:var(--oxford)]"/>}
          title="Plan &amp; billing"
          subtitle={`Current plan · ${user?.plan === "plus" ? "Plus" : (user?.plan === "edu" || user?.plan === "team") ? "Edu" : "Free"}`}
          defaultOpen={false}
          persistKey="billing"
          testId="section-billing"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm">Current plan</div>
              <div className="font-serif text-3xl mt-1">
                {user?.plan === "plus" ? "Plus" : user?.plan === "team" || user?.plan === "edu" ? "Edu" : "Free"}
              </div>
              <div className="text-xs text-[color:var(--ink-muted)] mt-1">
                {user?.plan === "free"
                  ? "Forever free · 5 decks · generous AI limits"
                  : "Thanks for supporting Ekkol — you have unlimited decks and priority AI."}
              </div>
            </div>
            <div className="flex gap-3">
              {user?.plan !== "free" ? (
                <button
                  onClick={cancelPlan}
                  disabled={canceling}
                  className="btn-ghost text-xs"
                  data-testid="btn-cancel-plan"
                >
                  {canceling ? "Switching…" : "Switch to Free plan"}
                </button>
              ) : (
                <Link to="/pricing" className="btn-primary text-sm" data-testid="btn-upgrade">
                  <Crown size={14}/> See Plus plans
                </Link>
              )}
            </div>
          </div>
          <p className="text-[11px] text-[color:var(--ink-muted)] mt-3 border-t rule pt-3">
            You can switch back anytime. Paid features stay active until the end of your current billing period.
            For invoice questions or refunds, email <a href="mailto:support@ekkol.app" className="link-underline">support@ekkol.app</a>.
          </p>
        </Collapsible>

        {/* Today's usage — moved down and collapsed by default since most users don't need it */}
        {usage && (
          <Collapsible
            icon={<ChartBar size={18} className="text-[color:var(--oxford)]"/>}
            title="Today&apos;s usage"
            subtitle="AI credits used today · only matters if you&apos;re hitting Free-tier caps"
            defaultOpen={false}
            persistKey="usage"
            testId="section-usage"
          >
            <div className="flex items-center justify-between mb-4 text-xs text-[color:var(--ink-muted)]">
              <span>Free plan caps reset at midnight UTC. Hit a cap? Ekkol keeps working — concept match falls back to fast local scoring automatically.</span>
            </div>
            <div className="space-y-3" data-testid="profile-usage">
              {Object.entries(usage.caps).map(([ep, cap]) => {
                const used = usage.used_today[ep] || 0;
                const unlimited = cap === null || cap === undefined;
                const pct = unlimited ? 0 : Math.min(100, Math.round((used / cap) * 100));
                const warning = !unlimited && pct >= 80;
                return (
                  <div key={ep} className="flex items-center gap-4 text-sm">
                    <div className="w-56 truncate">{ENDPOINT_LABELS[ep] || ep}</div>
                    <div className="flex-1 h-1.5 bg-[color:var(--surface-alt)] relative">
                      {!unlimited && (
                        <div className={`absolute inset-y-0 left-0 ${warning ? "bg-[color:var(--vermilion)]" : "bg-[color:var(--ink)]"}`} style={{width: `${pct}%`}}/>
                      )}
                    </div>
                    <div className="font-mono text-xs w-32 text-right">
                      {used} / {unlimited ? "∞" : cap}
                    </div>
                  </div>
                );
              })}
            </div>
          </Collapsible>
        )}

        {/* Danger zone — kept at bottom, always visible (but with a strong visual) */}
        <Collapsible
          icon={<WarningCircle size={18} className="text-[color:var(--vermilion)]"/>}
          title="Danger zone"
          subtitle="Export your data or permanently delete your account"
          defaultOpen={false}
          persistKey="danger"
          testId="danger-zone"
        >
          <p className="text-sm text-[color:var(--ink-muted)] mb-5">
            These actions are permanent. Export your data first if you&apos;d like a backup.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={exportData}
              disabled={exporting}
              className="btn-ghost justify-start text-left"
              data-testid="btn-export-data"
            >
              <ArrowRight size={14}/>
              <span>
                <span className="block font-mono text-xs uppercase tracking-widest">Download my data</span>
                <span className="block text-[11px] text-[color:var(--ink-muted)] normal-case">
                  JSON export of all decks, cards, and history
                </span>
              </span>
            </button>

            <button
              onClick={deleteAccount}
              className="inline-flex items-center gap-2 text-left bg-white hover:bg-[color:var(--vermilion)] hover:text-white border border-[color:var(--vermilion)] text-[color:var(--vermilion)] px-4 py-3 transition-colors"
              data-testid="btn-delete-account"
            >
              <Info size={14}/>
              <span>
                <span className="block font-mono text-xs uppercase tracking-widest">Delete my account</span>
                <span className="block text-[11px] opacity-70 normal-case">Permanent — cannot be undone</span>
              </span>
            </button>
          </div>
        </Collapsible>

        {/* Help / How to */}
        <div className="mt-10 border rule bg-white">
          <button
            onClick={() => setShowHelp((v) => !v)}
            className="w-full p-6 flex items-center justify-between"
            data-testid="help-toggle"
          >
            <div className="flex items-center gap-2">
              <Question size={18} className="text-[color:var(--oxford)]"/>
              <h2 className="font-serif text-2xl">How to get the most out of Ekkol</h2>
            </div>
            <span className="text-xs font-mono text-[color:var(--ink-muted)]">{showHelp ? "COLLAPSE" : "EXPAND"}</span>
          </button>
          {showHelp && (
            <div className="px-6 pb-8 space-y-6">
              <HelpStep n="01" icon={<GraduationCap size={18} weight="duotone"/>} title="Pick the right deck type">
                <p><strong>Script mode</strong> — you paste a speech, paragraph, or notes. We split it into chunks and you recite each one.</p>
                <p><strong>Q&amp;A → Statement</strong> — paste multiple-choice questions. AI rewrites each as a single declarative fact (e.g. &ldquo;The sky is blue.&rdquo;) that you repeat.</p>
                <p><strong>Q&amp;A → Flashcard</strong> — best for studying. App reads the question, you say the short answer.</p>
              </HelpStep>

              <HelpStep n="02" icon={<Target size={18} weight="duotone"/>} title="Tune the match mode & threshold">
                <p><strong>Exact</strong> — strict word overlap. Use this for terminology, numbers, dates, proper names.</p>
                <p><strong>Concept</strong> — AI judges meaning. You can rephrase freely (&ldquo;Blue is the color of the sky&rdquo; still passes). Use this for general knowledge.</p>
                <p>The <strong>pass threshold</strong> slider sets how strict scoring is. Start at 75% and adjust to taste.</p>
              </HelpStep>

              <HelpStep n="03" icon={<Headphones size={18} weight="duotone"/>} title="Hands-free mode for moving">
                <p>On the study screen, toggle <strong>Hands-free ON</strong>. Put on headphones. The app reads a chunk, listens for your response, scores it, and auto-advances on pass — or auto-repeats on fail.</p>
                <p>While in hands-free, say: <code className="font-mono bg-[color:var(--surface-alt)] px-1">next</code>, <code className="font-mono bg-[color:var(--surface-alt)] px-1">repeat</code>, <code className="font-mono bg-[color:var(--surface-alt)] px-1">pause</code>, or <code className="font-mono bg-[color:var(--surface-alt)] px-1">stop</code> to control without touching the screen.</p>
                <p>Perfect for walks, commutes, or chores.</p>
              </HelpStep>

              <HelpStep n="04" icon={<Sparkle size={18} weight="duotone"/>} title="Rebuild chunks as you progress">
                <p>On any script deck, click <strong>Rebuild chunks</strong> to change chunk size. Cognitive-science best practice:</p>
                <p>• Day 1 → <strong>Short</strong> (1 sentence, tight bites)</p>
                <p>• Day 2–3 → <strong>Medium</strong> (2–3 sentences, connect ideas)</p>
                <p>• Day 4+ → <strong>Long</strong> (full paragraphs, flow and delivery)</p>
              </HelpStep>

              <HelpStep n="05" icon={<SpeakerHigh size={18} weight="duotone"/>} title="Pick a voice you'll actually enjoy">
                <p>In the Settings gear during a session (or above), try each voice. Windows has Zira &amp; David. Mac has Samantha &amp; Karen. Chrome has Google US English voices. Pick whichever makes you want to listen.</p>
              </HelpStep>

              <HelpStep n="06" icon={<Info size={18} weight="duotone"/>} title="Share a deck with friends or a class">
                <p>Open any deck → toggle <strong>Public</strong> → copy the share link. Anyone with the link gets a read-only preview and a Sign-in CTA to practice. Perfect for teachers &amp; study groups.</p>
              </HelpStep>

              <HelpStep n="07" icon={<Ticket size={18} weight="duotone"/>} title="Free forever or unlock Plus">
                <p>Ekkol&apos;s core is free forever. Daily caps on AI help us stay sustainable — when you hit them, concept match gracefully falls back to local scoring so you can keep rehearsing.</p>
                <p>Plus removes caps, unlocks richer AI coaching, and premium OpenAI voices (coming soon). Have a voucher code? Redeem above.</p>
              </HelpStep>
            </div>
          )}
        </div>

        <div className="mt-10 flex justify-between items-center">
          <Link to="/dashboard" className="text-sm link-underline">← Back to library</Link>
          <Link to="/dashboard" className="btn-primary">
            Continue studying <ArrowRight size={16} weight="bold"/>
          </Link>
        </div>
      </main>
    </div>
  );
}

function HelpStep({ n, icon, title, children }) {
  return (
    <div className="flex gap-5" data-testid={`help-step-${n}`}>
      <div className="flex flex-col items-center">
        <div className="font-mono text-xs text-[color:var(--ink-muted)]">{n}</div>
        <div className="mt-2 text-[color:var(--oxford)]">{icon}</div>
      </div>
      <div className="flex-1 space-y-2 text-sm leading-relaxed text-[color:var(--ink-muted)]">
        <div className="font-serif text-lg text-[color:var(--ink)]">{title}</div>
        {children}
      </div>
    </div>
  );
}
